import React, { useId } from 'react';
import * as Styled from './OCardFeature.styles';
import {
  OCardFeatureProps,
  OCardFeatureContentAlignment,
  type OCardFeatureContentAlignmentInput,
  type OCardFeatureAHeaderInput,
  OCardFeatureImageScale,
  type OCardFeatureImageScaleInput,
  OCardFeatureLayout,
  type OCardFeatureLayoutInput,
} from './OCardFeature.types';
import { MTextBlockSimple } from '../../molecules/MTextBlockSimple';
import {
  MTextBlockSimpleAlignment,
  type MTextBlockSimpleProps,
} from '../../molecules/MTextBlockSimple/MTextBlockSimple.types';
import { AHeaderSize, AHeaderTag } from '../../atoms/AHeader/AHeader.types';
import { ABodySize, ABodyColor } from '../../atoms/ABody/ABody.types';
import { MButton } from '../../molecules/MButton';
import { MButtonType, MButtonSize } from '../../molecules/MButton/MButton.types';
import { MMediaSelector } from '../../molecules/MMediaSelector';
import { MMediaSelectorMediaType } from '../../molecules/MMediaSelector/MMediaSelector.types';
import type { MMediaSelectorProps } from '../../molecules/MMediaSelector/MMediaSelector.types';
import {
  AspectRatio,
  MediaQuery,
  ResponsiveImageSpec,
  ViewportPx,
  generateImageRenditions,
} from '../../utils/ImageUtils';

const COMPONENT_NAME = 'o-web-ui-kit-OCardFeature';
const TEST_ID = 'web-ui-kit-OCardFeature';

const DEFAULT_IMAGE_SCALE = OCardFeatureImageScale.L;

/**
 * Aspect ratio per breakpoint for each `imageScale`. Mobile covers
 * the XS/SM breakpoints (full-width, stacked), Tablet covers MD, and Desktop
 * covers LG/XL (each media column is at most half the page width).
 */
const IMAGE_SCALE_ASPECT_RATIO: Record<
  OCardFeatureImageScale,
  { mobile: AspectRatio; tablet: AspectRatio; desktop: AspectRatio }
> = {
  [OCardFeatureImageScale.L]: {
    mobile: AspectRatio.SQUARE,
    tablet: AspectRatio.SQUARE,
    desktop: AspectRatio.SQUARE,
  },
  [OCardFeatureImageScale.M]: {
    mobile: AspectRatio.CLASSIC_HOR,
    tablet: AspectRatio.SQUARE,
    desktop: AspectRatio.CLASSIC_HOR,
  },
  [OCardFeatureImageScale.S]: {
    mobile: AspectRatio.WIDE_HOR,
    tablet: AspectRatio.SQUARE,
    desktop: AspectRatio.WIDE_HOR,
  },
};

/**
 * Requested image widths per breakpoint. The media column is full width on
 * Mobile (XS/SM) but half width on Tablet (MD) and Desktop (LG/XL).
 */
const RENDITION_WIDTHS: { media: MediaQuery; imgWidthPx: number; band: 'mobile' | 'tablet' | 'desktop' }[] = [
  { media: MediaQuery.MIN_WIDTH_XS, imgWidthPx: ViewportPx.SM, band: 'mobile' },
  { media: MediaQuery.MIN_WIDTH_SM, imgWidthPx: ViewportPx.MD, band: 'mobile' },
  { media: MediaQuery.MIN_WIDTH_MD, imgWidthPx: ViewportPx.LG / 2, band: 'tablet' },
  { media: MediaQuery.MIN_WIDTH_LG, imgWidthPx: ViewportPx.XL / 2, band: 'desktop' },
  { media: MediaQuery.MIN_WIDTH_XL, imgWidthPx: ViewportPx.XXL / 2, band: 'desktop' },
];

const isImageScale = (value: string): value is OCardFeatureImageScale =>
  (Object.values(OCardFeatureImageScale) as string[]).includes(value);

const normalizeImageScale = (imageScale?: OCardFeatureImageScaleInput): OCardFeatureImageScale =>
  imageScale && isImageScale(imageScale) ? OCardFeatureImageScale[imageScale] : DEFAULT_IMAGE_SCALE;

const buildRenditionSpecs = (imageScale: OCardFeatureImageScale): ResponsiveImageSpec[] => {
  const ratios = IMAGE_SCALE_ASPECT_RATIO[imageScale];
  return RENDITION_WIDTHS.map(({ media, imgWidthPx, band }) => ({
    media,
    aspectRatio: ratios[band],
    imgWidthPx: Math.round(imgWidthPx),
  }));
};

/**
 * Injects imageScale-derived renditions into the supplied `mMediaSelector` for
 * the four supported media types.
 */
const applyImageScale = (
  mMediaSelector: MMediaSelectorProps,
  imageScale: OCardFeatureImageScale
): MMediaSelectorProps => {
  const renditionSpecs = buildRenditionSpecs(imageScale);

  switch (mMediaSelector.mediaType) {
    case MMediaSelectorMediaType.Image:
      return {
        ...mMediaSelector,
        mMediaImage: { ...mMediaSelector.mMediaImage, renditionSpecs },
      };

    case MMediaSelectorMediaType.Carousel:
      return {
        ...mMediaSelector,
        mMediaCarousel: {
          ...mMediaSelector.mMediaCarousel,
          renditionSpecs,
          slideRenditionSpecsResolver: undefined,
        },
      };

    case MMediaSelectorMediaType.CTAOverlay: {
      const mCtaThumbnail = mMediaSelector.mCtaThumbnail;
      if (!mCtaThumbnail) {
        return mMediaSelector;
      }
      const mOverlayCta = mCtaThumbnail.mOverlayCta;
      return {
        ...mMediaSelector,
        mCtaThumbnail: {
          ...mCtaThumbnail,
          aImage: {
            ...mCtaThumbnail.aImage,
            renditions: generateImageRenditions(renditionSpecs, mCtaThumbnail.aImage?.image?.assetPath),
          },
          // The CTA overlay text sits centered over the media in OCardFeature.
          // MTextBlockSimple defaults to Left, so center it here unless the
          // author has explicitly set an alignment.
          ...(mOverlayCta
            ? {
                mOverlayCta: {
                  ...mOverlayCta,
                  mTextBlockSimple: {
                    ...mOverlayCta.mTextBlockSimple,
                    alignment: mOverlayCta.mTextBlockSimple?.alignment ?? MTextBlockSimpleAlignment.Center,
                  },
                },
              }
            : {}),
        },
      };
    }

    case MMediaSelectorMediaType.ModalVideo: {
      const mVideoThumbnail = mMediaSelector.mVideoThumbnail;
      if (!mVideoThumbnail) {
        return mMediaSelector;
      }
      // Only the thumbnail image is reshaped here via responsive renditions.
      // The thumbnail box aspect ratio is driven from OCardFeature's own styles
      // (see MediaColumn) so it reshapes per breakpoint. The video plays in a
      // modal, so its aspect ratio is intentionally left untouched.
      return {
        ...mMediaSelector,
        mVideoThumbnail: {
          ...mVideoThumbnail,
          aImage: {
            ...mVideoThumbnail.aImage,
            renditions: generateImageRenditions(renditionSpecs, mVideoThumbnail.aImage?.image?.assetPath),
          },
        },
      };
    }

    default:
      return mMediaSelector;
  }
};

const isAemTrue = (value?: string): boolean => value === 'true';

const normalizeContentAlignment = (value?: OCardFeatureContentAlignmentInput): OCardFeatureContentAlignment =>
  value === OCardFeatureContentAlignment.Centered
    ? OCardFeatureContentAlignment.Centered
    : OCardFeatureContentAlignment.Left;

const normalizeLayout = (value?: OCardFeatureLayoutInput): OCardFeatureLayout =>
  value === OCardFeatureLayout.Reverse ? OCardFeatureLayout.Reverse : OCardFeatureLayout.Default;

const parseHeaderTag = (value?: OCardFeatureAHeaderInput['headerTag']): AHeaderTag | undefined => {
  if (!value) {
    return undefined;
  }
  const lowered = String(value).toLowerCase();
  return (Object.values(AHeaderTag) as string[]).includes(lowered) ? (lowered as AHeaderTag) : undefined;
};

export const OCardFeature = ({
  contentAlignment,
  layout,
  button1,
  button2,
  imageScale,
  mTextBlockSimple,
  mButton,
  mButton2,
  mMediaSelector,
  trackingProperties,
  ...rest
}: OCardFeatureProps) => {
  const generatedId = useId();
  const headerId = mTextBlockSimple?.aHeader?.id ?? `${generatedId}-header`;
  const hasHeaderText = !!mTextBlockSimple?.aHeader?.text?.trim();

  const resolvedContentAlignment = normalizeContentAlignment(contentAlignment);
  const resolvedLayout = normalizeLayout(layout);

  const textBlockAlignment =
    resolvedContentAlignment === OCardFeatureContentAlignment.Centered
      ? MTextBlockSimpleAlignment.Center
      : MTextBlockSimpleAlignment.Left;

  const showButton1 = isAemTrue(button1);
  const showButton2 = isAemTrue(button2);

  const resolvedImageScale = normalizeImageScale(imageScale);
  const resolvedMediaSelector = mMediaSelector ? applyImageScale(mMediaSelector, resolvedImageScale) : undefined;
  const resolvedTextBlock: MTextBlockSimpleProps = {
    ...mTextBlockSimple,
    alignment: textBlockAlignment,
    aHeader: {
      ...mTextBlockSimple?.aHeader,
      size: mTextBlockSimple?.aHeader?.size ?? AHeaderSize.TitleS,
      headerTag: parseHeaderTag(mTextBlockSimple?.aHeader?.headerTag),
      id: headerId,
    },
    aBody: {
      ...mTextBlockSimple?.aBody,
      size: mTextBlockSimple?.aBody?.size ?? ABodySize.S,
      color: mTextBlockSimple?.aBody?.color ?? ABodyColor.Alternate,
    },
  };

  const isModalVideo = resolvedMediaSelector?.mediaType === MMediaSelectorMediaType.ModalVideo;

  const mediaColumn = resolvedMediaSelector ? (
    <Styled.MediaColumn $thumbnailAspectRatioScale={isModalVideo ? resolvedImageScale : undefined}>
      <MMediaSelector {...resolvedMediaSelector} />
    </Styled.MediaColumn>
  ) : null;

  const contentColumn = (
    <Styled.ContentColumn $contentAlignment={resolvedContentAlignment}>
      <MTextBlockSimple {...resolvedTextBlock} />
      {(showButton1 || showButton2) && (
        <Styled.ButtonRow $contentAlignment={resolvedContentAlignment}>
          {showButton1 && (
            <MButton
              size={MButtonSize.S}
              {...mButton}
              buttonType={MButtonType.Primary}
              trackingProperties={{ ...trackingProperties, ...mButton?.trackingProperties }}
            />
          )}
          {showButton2 && (
            <MButton
              size={MButtonSize.S}
              {...mButton2}
              buttonType={MButtonType.Secondary}
              trackingProperties={{ ...trackingProperties, ...mButton2?.trackingProperties }}
            />
          )}
        </Styled.ButtonRow>
      )}
    </Styled.ContentColumn>
  );

  const isReverse = resolvedLayout === OCardFeatureLayout.Reverse;

  return (
    <Styled.Wrapper
      aria-labelledby={hasHeaderText ? headerId : undefined}
      {...rest}
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
    >
      {/*
        DOM order matches the desktop/tablet visual order so reading order and
        focus order stay in sync (WCAG 1.3.2 / 2.4.3):
        - Default: content first, media on the right.
        - Reverse: media first (on the left), content on the right.
        On mobile the media is always shown on top via a single order rule in
        the styles (see MediaColumn), which does not change the interactive
        sequence meaning between the two blocks.
      */}
      {isReverse ? (
        <>
          {mediaColumn}
          {contentColumn}
        </>
      ) : (
        <>
          {contentColumn}
          {mediaColumn}
        </>
      )}
    </Styled.Wrapper>
  );
};

OCardFeature.displayName = 'OCardFeature';

export { TEST_ID as O_CARD_FEATURE_TEST_ID };
